// Imports
import { cache } from "react";
import { unstable_cache } from "next/cache";
import { fetchClient } from "@/lib/fetch-client";
import {
  CACHE_TAGS,
  COMPETITIONS_QUERY_TAGS,
  FACILITIES_QUERY_TAGS,
  LOCATIONS_QUERY_TAGS,
  MYSHOW_QUERY_TAGS,
  SETTINGS_QUERY_TAGS,
  tagForCollection,
} from "@/lib/cacheTags";
import {
  SHOWBAGS_DATA,
  RIDES_DATA,
  ALL_EVENTS_DATA,
  COMPETITIONS_DATA,
  FOOD_DATA,
  LOCATIONS_DATA,
  FACILITIES_DATA,
  SETTINGS_DATA,
  MAP_ENABLED_SETTING,
  FOOTER_ROSE_DATA,
  SHOW_DATES,
  COMMERCIAL_EXHIBITORS,
  ALL_NEWS_DATA,
  MYSHOW_ENTRIES,
} from "@/lib/queries/global";
import {
  PAGE_DATA,
  PAGE_TITLE,
  COMPETITIONS_PAGE,
  EVENTS_WITH_COMPETITION_LINKS,
  FOOD_FINDER_PAGE,
  RIDES_PAGE,
  SHOWBAG_PAGE,
  NEWS_PAGE,
  COMMERCIAL_EXHIBITORS_PAGE,
  EVENTS_PAGE,
} from "@/lib/queries/page";
import {
  COMPETITION_UI,
  isLateEntriesPeriod,
  isLateEntriesTagTitle,
  isEntriesClosedTagTitle,
  shouldShowEntriesClosedTag,
} from "@/lib/competitionEntries";
import { resolveLink } from "@/lib/utils";
import {
  formatShowCalendarDayFilterLabel,
  formatShowCalendarDayFilterValue,
  formatShowCalendarIso,
  getShowDynamicCountdown,
  parseShowCalendarDate,
  sameShowCalendarDay,
  SHOW_DATE_LOCALE,
} from "@/lib/showDates";

// Server Side Helpers

// Unslugify function
export function unslugify(slug) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// Get Grid Data Based on Collection
export function getGridDataBasedOnCollection(
  showbags,
  events,
  rides,
  food,
  competitions,
  commercialExhibitors,
  news,
  collectionHandle,
  locations
) {
  switch (collectionHandle) {
    case "showbags":
      const showbags_data =
        locations?.length < 1
          ? showbags
          : showbags?.map((bag) => {
              const locationsArray = bag?.locations?.map(
                (location) => location?.title
              );

              const locationMatch = locationsArray.some((location) =>
                locations.some((loc) => loc.title === location)
              );

              if (locationMatch) {
                return bag;
              }
            });
      return {
        gridData: showbags_data,
        categories: [
          ...new Set(
            showbags?.flatMap((showbag) =>
              showbag?.categories?.map((category) => category?.title)
            )
          ),
        ].sort(),
        competitionTags: [],
      };

    case "events":
      const events_data =
        locations?.length < 1
          ? events
          : events?.map((event) => {
              const locationsArray = event?.locations?.map(
                (location) => location?.title
              );

              const locationMatch = locationsArray.some((location) =>
                locations.some((loc) => loc.title === location)
              );

              if (locationMatch) {
                return event;
              }
            });
      return {
        gridData: events_data,
        categories: [
          ...new Set(
            events?.flatMap((event) =>
              event?.categories?.map((category) => category?.title)
            )
          ),
        ].sort(),
        competitionTags: [],
      };

    case "rides":
      const rides_data =
        locations?.length < 1
          ? rides
          : rides?.map((ride) => {
              const locationsArray = ride?.locations?.map(
                (location) => location?.title
              );

              const locationMatch = locationsArray.some((location) =>
                locations.some((loc) => loc.title === location)
              );

              if (locationMatch) {
                return ride;
              }
            });
      return {
        gridData: rides_data,
        categories: [
          ...new Set(
            rides?.flatMap((ride) =>
              ride?.categories?.map((category) => category?.title)
            )
          ),
        ].sort(),
        competitionTags: [],
      };

    case "food_outlets":
      const food_outlets_data =
        locations?.length < 1
          ? food
          : food?.map((item) => {
              const locationsArray = item?.locations?.map(
                (location) => location?.title
              );

              const locationMatch = locationsArray.some((location) =>
                locations.some((loc) => loc.title === location)
              );

              if (locationMatch) {
                return item;
              }
            });
      return {
        gridData: food_outlets_data,
        categories: [
          ...new Set(
            food?.flatMap((food) =>
              food?.categories?.map((category) => category?.title)
            )
          ),
        ].sort(),
        competitionTags: [],
      };

    case "competitions":
      const competitions_data =
        locations?.length < 1
          ? competitions
          : competitions?.map((comp) => {
              const locationsArray = comp?.locations?.map(
                (location) => location?.title
              );

              const locationMatch = locationsArray.some((location) =>
                locations.some((loc) => loc.title === location)
              );

              if (locationMatch) {
                return comp;
              }
            });
      return {
        gridData: competitions_data,
        categories: [
          ...new Set(
            competitions?.flatMap((competition) =>
              competition?.categories?.map((category) => category?.title)
            )
          ),
        ].sort(),
        competitionTags: (() => {
          const titles = [
            ...new Set(
              competitions?.flatMap((competition) =>
                (competition?.tags ?? [])
                  .map((tag) => tag?.title)
                  .filter(Boolean)
              )
            ),
          ];
          const anyLate = competitions?.some(
            (c) =>
              c &&
              isLateEntriesPeriod(c.entries_close, c.late_entries_close)
          );
          if (
            anyLate &&
            !titles.some(isLateEntriesTagTitle)
          ) {
            titles.push(COMPETITION_UI.LATE_ENTRIES_LABEL);
          }
          const anyClosed = competitions?.some(
            (c) =>
              c &&
              shouldShowEntriesClosedTag(
                c.entries_open,
                c.entries_close,
                c.late_entries_close
              )
          );
          if (anyClosed && !titles.some(isEntriesClosedTagTitle)) {
            titles.push(COMPETITION_UI.ENTRIES_CLOSED_LABEL);
          }
          return titles.sort();
        })(),
      };

    case "commercial_exhibitors":
      const commercial_exhibitors_data =
        locations?.length < 1
          ? commercialExhibitors
          : commercialExhibitors?.map((commercialExhibitor) => {
              const locationsArray = commercialExhibitor?.locations?.map(
                (location) => location?.title
              );

              const locationMatch = locationsArray.some((location) =>
                locations.some((loc) => loc.title === location)
              );

              if (locationMatch) {
                return commercialExhibitor;
              }
            });
      return {
        gridData: commercial_exhibitors_data,
        categories: [
          ...new Set(
            commercialExhibitors?.flatMap((commercialExhibitor) =>
              commercialExhibitor?.exhibitor_types?.map(
                (category) => category?.title
              )
            )
          ),
        ].sort(),
        competitionTags: [],
      };
    case "news":
      return {
        gridData: news,
        categories: [],
        competitionTags: [],
      };

    default:
      return {
        gridData: [],
        categories: [],
        competitionTags: [],
      };
  }
}

// Get all showbags
export async function getAllShowbags() {
  try {
    const result = await fetchClient(SHOWBAGS_DATA, {}, "", "", [
      CACHE_TAGS.showbags,
    ]);
    return { showbags: result?.data?.entries?.data ?? [] };
  } catch (error) {
    console.error("getAllShowbags failed:", error?.message ?? error);
    return { showbags: [] };
  }
}

// Format date for Events Query
export function formatDateForEventsQuery() {
  // Get the current date
  const currentDate = new Date();

  // Add one day to the current date to get tomorrow's date
  const tomorrow = new Date(currentDate);
  tomorrow.setDate(currentDate.getDate() + 1);

  // Extract year, month, and day from tomorrow's date
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(tomorrow.getDate()).padStart(2, "0");

  // Construct the ISO 8601 date-time format string for tomorrow
  const tomorrowDateString = `${year}-${month}-${day}T00:00:00`;

  return tomorrowDateString;
}

// Get all events
export async function getAllEvents(token, livePreview) {
  try {
    const result = await fetchClient(ALL_EVENTS_DATA, {}, token, livePreview, [
      CACHE_TAGS.events,
    ]);
    return { allEvents: result?.data?.entries?.data ?? [] };
  } catch (error) {
    console.error("getAllEvents failed:", error?.message ?? error);
    return { allEvents: [] };
  }
}

// Get all rides
export async function getAllRides(token, livePreview) {
  try {
    const result = await fetchClient(RIDES_DATA, {}, token, livePreview, [
      CACHE_TAGS.rides,
    ]);

    return { rides: result?.data?.entries?.data ?? [] };
  } catch (error) {
    console.error("getAllRides failed:", error?.message ?? error);
    return { rides: [] };
  }
}

// Get all food
export async function getAllFood(token, livePreview) {
  try {
    const result = await fetchClient(FOOD_DATA, {}, token, livePreview, [
      CACHE_TAGS.food_outlets,
    ]);

    return { food: result?.data?.entries?.data ?? [] };
  } catch (error) {
    console.error("getAllFood failed:", error?.message ?? error);
    return { food: [] };
  }
}

// Get Page Data
// `unavailable: true` = CMS/GraphQL failure (show maintenance), not a real 404
export async function getPageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(PAGE_DATA, { slug }, token, livePreview, [
      CACHE_TAGS.pages,
    ]);

    // Missing host / empty payload — treat as outage, not a missing page
    if (!result || result.data == null) {
      console.error("getPageData unavailable: empty GraphQL payload", { slug });
      return { page: undefined, unavailable: true };
    }

    return {
      page: result.data.entry ?? undefined,
      unavailable: false,
    };
  } catch (error) {
    console.error("getPageData failed:", slug, error?.message ?? error);
    return { page: undefined, unavailable: true };
  }
}

// Get Page Title
export async function getPageTitle(slug, collection = "pages") {
  // Handle undefined, null, or invalid slug values
  if (!slug || slug === "undefined" || slug === "null") {
    return { title: "Royal Adelaide Show" };
  }

  try {
    const result = await fetchClient(
      PAGE_TITLE,
      { slug, collection },
      "",
      "",
      [tagForCollection(collection)]
    );
    return {
      title: result?.data?.entry?.title || "Royal Adelaide Show",
    };
  } catch (error) {
    console.error("getPageTitle failed:", slug, error?.message ?? error);
    return { title: "Royal Adelaide Show" };
  }
}

// Get Locations Taxonamy Terms
export async function getLocations(token, livePreview) {
  try {
    const locations = await fetchClient(
      LOCATIONS_DATA,
      {},
      token,
      livePreview,
      [...LOCATIONS_QUERY_TAGS]
    );

    return { locations: locations?.data?.terms?.data };
  } catch (error) {
    console.error("getLocations failed:", error?.message ?? error);
    return { locations: [] };
  }
}

// Get Facilties Taxonamy Terms
export async function getFacilties(token, livePreview) {
  try {
    const facilities = await fetchClient(
      FACILITIES_DATA,
      {},
      token,
      livePreview,
      [...FACILITIES_QUERY_TAGS]
    );

    return { facilities: facilities?.data?.terms?.data };
  } catch (error) {
    console.error("getFacilties failed:", error?.message ?? error);
    return { facilities: [] };
  }
}

function hexToRgb(hex, fallback = "21 21 21") {
  if (!hex || typeof hex !== "string") {
    return fallback;
  }

  // Remove the hash (#) symbol if present
  hex = hex.replace(/^#/, "");

  if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
    return fallback;
  }

  // Parse the hex string to get the RGB values
  let bigint = parseInt(hex, 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;

  return `${r} ${g} ${b}`;
}

// Brand fallbacks when CMS theme/globals are unavailable (avoids white-on-white).
const THEME_FALLBACKS = {
  background: "#E7E4D9", // brand-wool
  foreground: "#151515", // brand-espresso
  buttonBackground: "#892102", // brand-red-gum
  buttonForeground: "#FFFFFF",
  cardBackground: "#FFFFFF",
  cardForeground: "#151515",
  headerBackground: "#E7E4D9",
  headerForeground: "#151515",
  highlightBackground: "#892102",
  highlightForeground: "#FFFFFF",
};

//  Get Theme Styles
export function getThemeStyles(theme) {
  const buttonBg = theme?.button_background_color || THEME_FALLBACKS.buttonBackground;
  const isRedGum =
    buttonBg.replace(/^#/, "").toLowerCase() === "892102";

  return {
    "--background": hexToRgb(
      theme?.background_color || THEME_FALLBACKS.background,
      "231 228 217"
    ),
    "--foreground": hexToRgb(
      theme?.foreground_color || THEME_FALLBACKS.foreground,
      "21 21 21"
    ),

    "--button-background": hexToRgb(buttonBg, "137 33 2"),
    // Solid red gum buttons always use white labels
    "--button-foreground": hexToRgb(
      isRedGum
        ? THEME_FALLBACKS.buttonForeground
        : theme?.button_foreground_color || THEME_FALLBACKS.buttonForeground,
      "255 255 255"
    ),
    "--button-hover-background": hexToRgb(
      // Red gum (#892102) ↔ saddle orange (#FC652E)
      isRedGum ? "#FC652E" : "#892102",
      "252 101 46"
    ),
    // Hover fill is red gum (or orange); white keeps labels readable on both
    "--button-hover-foreground": hexToRgb("#FFFFFF", "255 255 255"),

    "--button-radius": (theme?.button_border_radius ?? 0) + "px",
    "--button-border-width": (theme?.button_border_width ?? 0) + "px",

    "--card-background": hexToRgb(
      theme?.card_background_color || THEME_FALLBACKS.cardBackground,
      "255 255 255"
    ),
    "--card-foreground": hexToRgb(
      theme?.card_foreground_color || THEME_FALLBACKS.cardForeground,
      "21 21 21"
    ),

    "--card-radius": (theme?.card_border_radius ?? 0) + "px",
    "--card-border-width": (theme?.card_border_width ?? 0) + "px",

    "--header-background": hexToRgb(
      theme?.header_background_color || THEME_FALLBACKS.headerBackground,
      "231 228 217"
    ),
    "--header-foreground": hexToRgb(
      theme?.header_foreground_color || THEME_FALLBACKS.headerForeground,
      "21 21 21"
    ),

    "--highlight-background": hexToRgb(
      theme?.highlights_background_color || THEME_FALLBACKS.highlightBackground,
      "137 33 2"
    ),
    "--highlight-foreground": hexToRgb(
      theme?.highlights_foreground_color || THEME_FALLBACKS.highlightForeground,
      "255 255 255"
    ),
  };
}



// Fallback when GraphQL is unavailable (misconfigured env, CMS down during build, etc.)
function getEmptySettingsData() {
  return {
    showModeEnabled: false,
    mapFile: undefined,
    header: {
      showModeNav: undefined,
      outOfShowModeNav: undefined,
      showModeEnabled: false,
      mainLogo: undefined,
      showSearch: undefined,
      showIcon: undefined,
      showBuyTickets: undefined,
      buyTicketsLink: undefined,
    },
    theme: undefined,
    banner: {
      showBanner: false,
      title: undefined,
      content: undefined,
      link: undefined,
      titleColor: undefined,
      backgroundColor: undefined,
    },
    footer: {
      showModeNav: undefined,
      outOfShowModeNav: undefined,
      showModeEnabled: false,
      mainLogo: undefined,
      footerRose: undefined,
      subLogo: undefined,
      socials: undefined,
      contentRight: undefined,
      contentBottom: undefined,
      address: undefined,
      name: undefined,
      phone: undefined,
      email: undefined,
    },

  };
}

// Get Settings Data — React.cache() dedupes within a single request (layout + page)
export const getSettingsData = cache(async function getSettingsData(
  token,
  livePreview
) {
  try {
    const result = await fetchClient(SETTINGS_DATA, {}, token, livePreview, [
      ...SETTINGS_QUERY_TAGS,
    ]);

    // Soft-fetch: staging CMS may not expose footer_rose until rebrand is deployed
    let footerRose = undefined;
    try {
      const roseResult = await fetchClient(
        FOOTER_ROSE_DATA,
        {},
        token,
        livePreview,
        [CACHE_TAGS.globals]
      );
      footerRose = roseResult?.data?.globalSet?.footer_rose;
    } catch {
      footerRose = undefined;
    }

    return {
      showModeEnabled: result?.data?.globalSet?.enable_show_mode,
      mapFile: result?.data?.globalSet?.file,
      header: {
        showModeNav: result?.data?.globalSet?.show_mode_main_nav?.tree,
        outOfShowModeNav:
          result?.data?.globalSet?.out_of_show_mode_main_nav?.tree,
        showModeEnabled: result?.data?.globalSet?.enable_show_mode,
        mainLogo: result?.data?.globalSet?.main_logo,
        showSearch: result?.data?.globalSet?.show_search,
        showIcon: result?.data?.globalSet?.show_search_icon,
        showBuyTickets: result?.data?.globalSet?.show_buy,
        buyTicketsLink: resolveLink(result?.data?.globalSet?.buy_tickets_link),
      },
      theme: result?.data?.globalSet?.current_theme,
      banner: {
        showBanner: result?.data?.globalSet?.show_banner,
        title: result?.data?.globalSet?.banner?.title,
        content: result?.data?.globalSet?.banner?.content,
        link: resolveLink(result?.data?.globalSet?.banner?.link),
        titleColor: result?.data?.globalSet?.banner?.title_text_color,
        backgroundColor: result?.data?.globalSet?.banner?.background_color,
      },
      footer: {
        showModeNav: result?.data?.globalSet?.show_mode_footer_nav?.tree,
        outOfShowModeNav:
          result?.data?.globalSet?.out_of_show_mode_footer_nav?.tree,
        showModeEnabled: result?.data?.globalSet?.enable_show_mode,
        mainLogo: result?.data?.globalSet?.main_footer_logo,
        footerRose,
        subLogo: result?.data?.globalSet?.sub_logo,
        socials: result?.data?.globalSet?.socials?.map((social) => ({
          ...social,
          link: resolveLink(social?.link),
        })),
        contentRight: result?.data?.globalSet?.content_right,
        contentBottom: result?.data?.globalSet?.content_bottom,
        address: result?.data?.globalSet?.address,
        name: result?.data?.globalSet?.name,
        phone: result?.data?.globalSet?.phone,
        email: result?.data?.globalSet?.email,
      },
    };
  } catch (error) {
    console.error("getSettingsData failed:", error?.message ?? error);
    return getEmptySettingsData();
  }
});

function getEmptyShowDates() {
  return {
    daysUntil: 0,
    startDate: "",
    endDate: "",
    showStartDate: null,
    unformattedStartDate: null,
    unformattedEndDate: null,
    showStartTime: 0,
    testMode: false,
    testDate: null,
    showCount: false,
  };
}

// Get Show Dates — React.cache() dedupes within a single request
export const getShowDates = cache(async function getShowDates(
  token,
  livePreview
) {
  try {
    const result = await fetchClient(SHOW_DATES, {}, token, livePreview, [
      CACHE_TAGS.globals,
    ]);
    const gs = result?.data?.globalSet;
    // Empty show_dates is a valid off-season / pre-announce state
    if (!gs?.show_dates?.start || !gs?.show_dates?.end) {
      return getEmptyShowDates();
    }
    const showStartCalendarDate = parseShowCalendarDate(gs.show_dates.start);
    const showEndCalendarDate = parseShowCalendarDate(gs.show_dates.end);
    const { days: daysUntil } = getShowDynamicCountdown(
      gs.show_dates.start,
      parseInt(gs.show_start_time?.split(":")?.[0] ?? "0", 10)
    );
    const startDate =
      showStartCalendarDate?.toLocaleDateString(SHOW_DATE_LOCALE, {
        month: "long",
        day: "numeric",
      }) ?? "";
    const endDate =
      showEndCalendarDate?.toLocaleDateString(SHOW_DATE_LOCALE, {
        month: "long",
        day: "numeric",
      }) ?? "";
    const showStartDate = showStartCalendarDate;
    const showStartTime = parseInt(
      gs.show_start_time?.split(":")?.[0] ?? "0",
      10
    );

    return {
      daysUntil,
      startDate,
      endDate,
      showStartDate,
      unformattedStartDate: gs.show_dates.start,
      unformattedEndDate: gs.show_dates.end,
      showStartTime,
      testMode: gs.test_mode,
      testDate: gs.test_date,
      showCount: gs.show_count,
    };
  } catch (error) {
    console.error("getShowDates failed:", error?.message ?? error);
    return getEmptyShowDates();
  }
});

// Get Theme Colors
export async function getThemeColors(token, livePreview) {
  try {
    const result = await fetchClient(SETTINGS_DATA, {}, token, livePreview, [
      CACHE_TAGS.globals,
      CACHE_TAGS.themes,
    ]);

    return {
      primary:
        result?.data?.globalSet?.current_theme?.primary_button_background_color,
      secondary:
        result?.data?.globalSet?.current_theme
          ?.secondary_button_background_color,
      tertiary:
        result?.data?.globalSet?.current_theme?.tertiary_button_background_color,
      alternate:
        result?.data?.globalSet?.current_theme
          ?.alternate_button_background_color,
    };
  } catch (error) {
    console.error("getThemeColors failed:", error?.message ?? error);
    return {
      primary: undefined,
      secondary: undefined,
      tertiary: undefined,
      alternate: undefined,
    };
  }
}

// Get All Competitions
export async function getAllCompetitions(token, livePreview) {
  try {
    const result = await fetchClient(COMPETITIONS_DATA, {}, token, livePreview, [
      ...COMPETITIONS_QUERY_TAGS,
    ]);

    return { competitions: result?.data?.entries?.data ?? [] };
  } catch (error) {
    console.error("getAllCompetitions failed:", error?.message ?? error);
    return { competitions: [] };
  }
}

function mergeCompetitionScheduleEvents(directEvents = [], linkedEvents = []) {
  const byId = new Map();
  [...directEvents, ...linkedEvents].forEach((event) => {
    if (!event?.id) return;
    byId.set(event.id, event);
  });
  return Array.from(byId.values());
}

async function getEventsLinkedToCompetition(
  competitionId,
  competitionSlug,
  token,
  livePreview
) {
  try {
    const result = await fetchClient(
      EVENTS_WITH_COMPETITION_LINKS,
      {},
      token,
      livePreview,
      [...COMPETITIONS_QUERY_TAGS]
    );

    return (result?.data?.entries?.data ?? []).filter((event) =>
      (event.competition ?? []).some(
        (competition) =>
          competition?.id === competitionId ||
          competition?.slug === competitionSlug
      )
    );
  } catch (error) {
    console.error(
      "getEventsLinkedToCompetition failed:",
      competitionSlug,
      error?.message ?? error
    );
    return [];
  }
}

// Get Single Competition Page
export async function getCompetitionPageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(
      COMPETITIONS_PAGE,
      { slug },
      token,
      livePreview,
      [...COMPETITIONS_QUERY_TAGS]
    );

    const page = result?.data?.entry;
    if (!page) {
      return { page: undefined };
    }

    // Include events that select this competition on the event entry
    // (e.g. Every day “Wool on Display”), not only competition.events.
    const linkedEvents = await getEventsLinkedToCompetition(
      page.id,
      page.slug ?? slug,
      token,
      livePreview
    );
    page.events = mergeCompetitionScheduleEvents(page.events, linkedEvents);

    return { page };
  } catch (error) {
    console.error(
      "getCompetitionPageData failed:",
      slug,
      error?.message ?? error
    );
    return { page: undefined };
  }
}

// Get Single Food Finder Page
export async function getFoodFinderPageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(
      FOOD_FINDER_PAGE,
      { slug },
      token,
      livePreview,
      [CACHE_TAGS.food_outlets]
    );

    return { page: result?.data?.entry };
  } catch (error) {
    console.error(
      "getFoodFinderPageData failed:",
      slug,
      error?.message ?? error
    );
    return { page: undefined };
  }
}

export async function getCommercialExhibitorPageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(
      COMMERCIAL_EXHIBITORS_PAGE,
      { slug },
      token,
      livePreview,
      [CACHE_TAGS.commercial_exhibitors]
    );

    return { page: result?.data?.entry };
  } catch (error) {
    console.error(
      "getCommercialExhibitorPageData failed:",
      slug,
      error?.message ?? error
    );
    return { page: undefined };
  }
}

// Get Single Event Page
export async function getEventPageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(EVENTS_PAGE, { slug }, token, livePreview, [
      CACHE_TAGS.events,
    ]);

    return { page: result?.data?.entry };
  } catch (error) {
    console.error("getEventPageData failed:", slug, error?.message ?? error);
    return { page: undefined };
  }
}

// Get Single Ride Page
export async function getRidePageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(RIDES_PAGE, { slug }, token, livePreview, [
      CACHE_TAGS.rides,
    ]);

    return { page: result?.data?.entry };
  } catch (error) {
    console.error("getRidePageData failed:", slug, error?.message ?? error);
    return { page: undefined };
  }
}

// Get Single Showbag Page
export async function getShowbagPageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(SHOWBAG_PAGE, { slug }, token, livePreview, [
      CACHE_TAGS.showbags,
    ]);

    return { page: result?.data?.entry };
  } catch (error) {
    console.error("getShowbagPageData failed:", slug, error?.message ?? error);
    return { page: undefined };
  }
}

// Get All News
export async function getAllNews(slug, token, livePreview) {
  try {
    const result = await fetchClient(
      ALL_NEWS_DATA,
      { slug },
      token,
      livePreview,
      [CACHE_TAGS.news]
    );

    return { news: result?.data?.entries?.data ?? [] };
  } catch (error) {
    console.error("getAllNews failed:", error?.message ?? error);
    return { news: [] };
  }
}

// Get Single News Page
export async function getNewsPageData(slug, token, livePreview) {
  try {
    const result = await fetchClient(NEWS_PAGE, { slug }, token, livePreview, [
      CACHE_TAGS.news,
    ]);

    return { page: result?.data?.entry };
  } catch (error) {
    console.error("getNewsPageData failed:", slug, error?.message ?? error);
    return { page: undefined };
  }
}

export async function getAllCommercialExhibitors(slug, token, livePreview) {
  try {
    const result = await fetchClient(
      COMMERCIAL_EXHIBITORS,
      { slug },
      token,
      livePreview,
      [CACHE_TAGS.commercial_exhibitors]
    );

    return { commercialExhibitors: result?.data?.entries?.data ?? [] };
  } catch (error) {
    console.error(
      "getAllCommercialExhibitors failed:",
      error?.message ?? error
    );
    return { commercialExhibitors: [] };
  }
}

export function getDaysOfShowInArray({ startDate, endDate }) {
  if (!startDate || !endDate) {
    return [];
  }

  const start = parseShowCalendarDate(startDate);
  const end = parseShowCalendarDate(endDate);
  if (!start || !end) {
    return [];
  }

  const days = [];
  const currentDate = new Date(start);

  while (currentDate <= end) {
    days.push({
      value: formatShowCalendarDayFilterValue(currentDate),
      label: formatShowCalendarDayFilterLabel(currentDate),
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
}

export async function getDaysWithWeatherForecast(days) {
  // Fetch weather data, only fetch if we have env vars

  if (process.env.WEATHER_API_URL || process.env.WEATHER_API_KEY) {
    const response = await fetch(
      `${process.env.WEATHER_API_URL}?key=${process.env.WEATHER_API_KEY}&q=-34.945769,138.586647&days=14`,
      { next: { revalidate: 3600, tags: [CACHE_TAGS.weather] } }
    );
    if (response.ok) {
      const data = await response.json();
      days.map((day) => {
        data.forecast.forecastday.map((forecastDay) => {
          if (sameShowCalendarDay(forecastDay.date, day.value.date)) {
            day.forecast = forecastDay;
          }
        });
      });
      return days;
    } else {
      return { message: "Failed to fetch data" };
    }
  } else {
    return { message: "Missing ENV vars for weather" };
  }
}

export function getDatesEventIsOn(event, showDates) {
  const datesWithTime = [];

  const { startDate: showStartDate, endDate: showEndDate } = showDates ?? {};

  if (event?.every_day) {
    const startDate = parseShowCalendarDate(showStartDate);
    const endDate = parseShowCalendarDate(showEndDate);

    if (!startDate || !endDate) {
      return datesWithTime;
    }

    const startTimeParts = event.start_time ? event.start_time.split(":") : [];
    const endTimeParts = event.end_time ? event.end_time.split(":") : [];

    const startHour =
      startTimeParts.length === 2 ? parseInt(startTimeParts[0]) : null;
    const startMinute =
      startTimeParts.length === 2 ? parseInt(startTimeParts[1]) : null;
    const endHour =
      endTimeParts.length === 2 ? parseInt(endTimeParts[0]) : null;
    const endMinute =
      endTimeParts.length === 2 ? parseInt(endTimeParts[1]) : null;

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      let start = new Date(currentDate);
      if (startHour !== null && startMinute !== null) {
        start.setHours(startHour, startMinute);
      }

      let end = new Date(currentDate);
      if (endHour !== null && endMinute !== null) {
        end.setHours(endHour, endMinute);
      }

      // Check if the date is not in the past of today's date
      if (true) {
        datesWithTime.push({
          date: formatDate(currentDate),
          showEndTime: !event.hide_end_time,
          start_time:
            startHour !== null && startMinute !== null
              ? formatTime(start)
              : null,
          end_time:
            endHour !== null && endMinute !== null ? formatTime(end) : null,
          all_day: event.all_day, // Include all_day property
        });
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else if (event?.multi_day && event?.days && event?.days?.length > 0) {
    event.days.forEach((day) => {
      let dayDate = parseShowCalendarDate(day?.date);
      if (!dayDate) {
        return; // Skip this iteration
      }

      let start = new Date(dayDate);
      let end = new Date(dayDate);

      // If all_day is true, set start and end times to null
      if (day.all_day_multi_day) {
        start = null;
        end = null;
      } else {
        if (day.start_time) {
          const startTimeParts = day.start_time.split(":");
          if (
            startTimeParts.length !== 2 ||
            isNaN(parseInt(startTimeParts[0])) ||
            isNaN(parseInt(startTimeParts[1]))
          ) {
            return; // Skip this iteration
          }
          let startHour = parseInt(startTimeParts[0]);
          let startMinute = parseInt(startTimeParts[1]);
          start.setHours(startHour, startMinute);
        }

        if (day.end_time) {
          const endTimeParts = day.end_time.split(":");
          if (
            endTimeParts.length !== 2 ||
            isNaN(parseInt(endTimeParts[0])) ||
            isNaN(parseInt(endTimeParts[1]))
          ) {
            return; // Skip this iteration
          }
          let endHour = parseInt(endTimeParts[0]);
          let endMinute = parseInt(endTimeParts[1]);
          end.setHours(endHour, endMinute);
        }
      }

      // Check if the date is not in the past of today's date
      // if (dayDate.getDate() >= today.getDate()) {
      datesWithTime.push({
        date: formatDate(dayDate),
        showEndTime: !day?.hide_end_time,
        start_time: day.all_day_multi_day
          ? true
          : day.start_time
          ? formatTime(start)
          : null,
        end_time: day.all_day_multi_day
          ? true
          : day.end_time
          ? formatTime(end)
          : null,
        all_day: day.all_day_multi_day, // Include all_day property
      });
      // }
    });
  } else {
    const startDate = parseShowCalendarDate(event?.start_date);
    const endDate = parseShowCalendarDate(event?.end_date);

    if (!startDate || !endDate) {
      return datesWithTime;
    }

    const startTimeParts = event.start_time ? event.start_time.split(":") : [];
    const endTimeParts = event.end_time ? event.end_time.split(":") : [];

    const startHour =
      startTimeParts.length === 2 ? parseInt(startTimeParts[0]) : null;
    const startMinute =
      startTimeParts.length === 2 ? parseInt(startTimeParts[1]) : null;
    const endHour =
      endTimeParts.length === 2 ? parseInt(endTimeParts[0]) : null;
    const endMinute =
      endTimeParts.length === 2 ? parseInt(endTimeParts[1]) : null;

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      let start = new Date(currentDate);
      if (startHour !== null && startMinute !== null) {
        start.setHours(startHour, startMinute);
      }

      let end = new Date(currentDate);
      if (endHour !== null && endMinute !== null) {
        end.setHours(endHour, endMinute);
      }

      datesWithTime.push({
        date: formatDate(currentDate),
        showEndTime: !event.hide_end_time,
        start_time:
          startHour !== null && startMinute !== null
            ? formatTime(start)
            : null,
        end_time:
          endHour !== null && endMinute !== null ? formatTime(end) : null,
        all_day: event.all_day, // Include all_day property
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return datesWithTime;
}

export async function getMyShowEntries(slugs) {
  const result = await fetchClient(MYSHOW_ENTRIES, { slugs }, "", "", [
    ...MYSHOW_QUERY_TAGS,
  ]);

  return result?.data;
}

export function getEventTimesByDay(dates, selectedDay) {
  if (selectedDay.slug == "all-days") {
    return dates;
  } else {
    return dates?.filter((day) =>
      sameShowCalendarDay(day.date, selectedDay.date)
    );
  }
}

// Format Date
function formatDate(date) {
  return formatShowCalendarIso(date);
}

// Format Time
function formatTime(time) {
  const hours = time.getHours().toString().padStart(2, "0");
  const minutes = time.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

async function fetchIsMapActive(token, livePreview) {
  try {
    const result = await fetchClient(
      MAP_ENABLED_SETTING,
      {},
      token,
      livePreview,
      [...SETTINGS_QUERY_TAGS]
    );
    return {
      mapIsActive: Boolean(result?.data?.globalSet?.enable_map),
    };
  } catch (error) {
    // Field missing (pre-deploy) or CMS unavailable — keep map links off
    console.warn(
      "getIsMapActive failed, defaulting map off:",
      error?.message ?? error
    );
    return { mapIsActive: false };
  }
}

const getCachedIsMapActive = unstable_cache(
  () => fetchIsMapActive("", false),
  ["settings-map-enabled"],
  {
    tags: [...SETTINGS_QUERY_TAGS],
    revalidate: 86400,
  }
);

export async function getIsMapActive(token, livePreview) {
  // Preview requests must always reflect the editor's current CMS state. Public
  // requests can share one cached fallback/result across the entire prerender
  // rather than repeating this global setting query for every generated page.
  if (token || livePreview) {
    return fetchIsMapActive(token, livePreview);
  }

  return getCachedIsMapActive();
}
